<template>
  <div class="staff-person-modal" @click.self="close" @keydown.esc="close">
    <div class="staff-person-modal__card" role="dialog" aria-modal="true">
      <button type="button" class="staff-person-modal__close" aria-label="Закрыть" @click="close">
        ×
      </button>

      <div class="staff-person-modal__layout">
        <div v-if="person.image" class="staff-person-modal__photo">
          <img :src="person.image" :alt="person.name || ''">
        </div>

        <div class="staff-person-modal__info">
          <h3 v-if="fullName" class="staff-person-modal__name">{{ fullName }}</h3>

          <dl class="staff-person-modal__fields">
            <div v-if="position" class="staff-person-modal__row">
              <dt>Должность:</dt>
              <dd>{{ position }}</dd>
            </div>
            <div v-if="mainDepartment" class="staff-person-modal__row">
              <dt>Подразделение:</dt>
              <dd>{{ mainDepartment }}</dd>
            </div>
            <div v-if="subDepartment" class="staff-person-modal__row">
              <dt>Отделение:</dt>
              <dd>{{ subDepartment }}</dd>
            </div>
            <div v-if="medicalExperienceLine" class="staff-person-modal__row">
              <dt>Медицинский стаж:</dt>
              <dd>{{ medicalExperienceLine }}</dd>
            </div>
            <div v-if="qualificationCategory" class="staff-person-modal__row">
              <dt>Категория:</dt>
              <dd>{{ qualificationCategory }}</dd>
            </div>
            <div v-if="academicDegree" class="staff-person-modal__row">
              <dt>Учёная степень:</dt>
              <dd>{{ academicDegree }}</dd>
            </div>
            <div
              v-if="diplomaLines.length"
              class="staff-person-modal__row staff-person-modal__row--diploma"
            >
              <dt>Диплом:</dt>
              <dd>
                <span
                  v-for="(line, idx) in diplomaLines"
                  :key="'dip-' + idx"
                  class="staff-person-modal__diploma-line"
                >{{ line }}</span>
              </dd>
            </div>
            <div
              v-if="accreditationLines.length"
              class="staff-person-modal__row staff-person-modal__row--accreditation"
            >
              <dt>Аккредитация:</dt>
              <dd>
                <span
                  v-for="(line, idx) in accreditationLines"
                  :key="'acc-' + idx"
                  class="staff-person-modal__accreditation-line"
                >{{ line }}</span>
              </dd>
            </div>
            <div v-if="openingHours" class="staff-person-modal__row">
              <dt>Часы приёма:</dt>
              <dd>{{ openingHours }}</dd>
            </div>
          </dl>

          <div class="staff-person-modal__reviews">
            <div class="staff-person-modal__reviews-head">
              <h4 class="staff-person-modal__reviews-title">Отзывы</h4>
              <router-link
                class="staff-person-modal__reviews-link"
                :to="{ path: '/about/reviews', query: { employeeId: person.id } }"
              >Все отзывы</router-link>
            </div>
            <p v-if="reviewsLoading" class="staff-person-modal__reviews-empty">Загрузка…</p>
            <template v-else-if="personReviews.length">
              <article
                v-for="rev in personReviews"
                :key="rev.id"
                class="staff-person-modal__review"
              >
                <div class="staff-person-modal__review-meta">
                  <span v-if="rev.rating" class="staff-person-modal__review-stars" aria-label="Оценка">
                    <span
                      v-for="star in 5"
                      :key="star"
                      :class="{ 'is-active': star <= rev.rating }"
                    >★</span>
                  </span>
                  <span class="staff-person-modal__review-date">{{ formatReviewDate(rev.date) }}</span>
                </div>
                <p class="staff-person-modal__review-text">«{{ truncateReview(rev.review) }}»</p>
              </article>
            </template>
            <p v-else class="staff-person-modal__reviews-empty">
              Отзывов о специалисте пока нет.
            </p>
            <router-link
              class="staff-person-modal__reviews-cta"
              :to="{ path: '/about/reviews/leave', query: { employeeId: person.id, employeeName: person.name } }"
            >Оставить отзыв</router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fullYearsSinceMedicalExperienceStart,
  yearsDeclensionRu,
} from "@/helpers/medicalExperience";
import reviewsApi from "@/api/reviews";

const MONTHS_RU = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

export default {
  name: "StaffPersonModal",
  props: {
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      personReviews: [],
      reviewsLoading: false,
    };
  },
  computed: {
    nameParts() {
      const parts = String(this.person.name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      return {
        surname: parts[0] || "",
        firstName: parts[1] || "",
        patronymic: parts.slice(2).join(" "),
      };
    },
    surname() {
      return this.nameParts.surname;
    },
    firstName() {
      return this.nameParts.firstName;
    },
    patronymic() {
      return this.nameParts.patronymic;
    },
    fullName() {
      return [this.surname, this.firstName, this.patronymic]
        .filter(Boolean)
        .join(" ");
    },
    position() {
      return String(this.person.position || "").trim();
    },
    medicalExperienceLine() {
      const start = this.person.medicalExperienceStartDate;
      if (!start) return "";
      const years = fullYearsSinceMedicalExperienceStart(start);
      if (years === null) return "";
      return `${years} ${yearsDeclensionRu(years)}`;
    },
    qualificationCategory() {
      return String(
        this.person.qualificationCategory ||
          this.person.qualification_category ||
          this.person.category ||
          ""
      ).trim();
    },
    academicDegree() {
      return String(
        this.person.academicDegree || this.person.academic_degree || ""
      ).trim();
    },
    medicalEducationInstitution() {
      return String(this.person.medicalEducationInstitution || "").trim();
    },
    medicalEducationQualification() {
      return String(this.person.medicalEducationQualification || "").trim();
    },
    medicalEducationSpecialty() {
      return String(this.person.medicalEducationSpecialty || "").trim();
    },
    medicalEducationIssueDateLine() {
      const raw = String(this.person.medicalEducationIssueDate || "").trim();
      if (!raw) return "";
      return this.formatIsoDateRu(raw);
    },
    medicalEducationsList() {
      const list = Array.isArray(this.person.medicalEducations)
        ? this.person.medicalEducations
        : [];
      const fromList = list
        .map((e) => ({
          specialty: String(e?.specialty || "").trim(),
          institution: String(e?.institution || "").trim(),
          startDate: String(e?.startDate || "").trim(),
          endDate: String(e?.endDate || "").trim(),
        }))
        .filter((e) => e.specialty || e.institution || e.startDate || e.endDate);
      if (fromList.length) return fromList;
      if (
        this.medicalEducationInstitution ||
        this.medicalEducationQualification ||
        this.medicalEducationSpecialty ||
        this.medicalEducationIssueDateLine
      ) {
        return [
          {
            specialty:
              this.medicalEducationSpecialty ||
              this.medicalEducationQualification ||
              "",
            institution: this.medicalEducationInstitution,
            startDate: "",
            endDate: String(this.person.medicalEducationIssueDate || "").trim(),
          },
        ];
      }
      return [];
    },
    hasMedicalEducation() {
      return this.medicalEducationsList.length > 0;
    },
    diplomaLines() {
      return this.medicalEducationsList.map((e) => {
        const periodParts = [];
        if (e.startDate) periodParts.push(this.formatIsoDateRu(e.startDate));
        if (e.endDate) periodParts.push(this.formatIsoDateRu(e.endDate));
        const period = periodParts.join(" – ");
        return [e.specialty, e.institution, period].filter(Boolean).join(", ");
      }).filter(Boolean);
    },
    accreditationLines() {
      const list = Array.isArray(this.person.accreditations)
        ? this.person.accreditations
        : [];
      return list
        .map((a) => {
          const specialty = String(a?.specialty || "").trim();
          const raw = String(a?.validUntil || "").trim();
          let validUntilLine = "";
          if (raw) {
            const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (m) {
              const day = Number(m[3]);
              const monthIdx = Number(m[2]) - 1;
              const year = m[1];
              validUntilLine =
                monthIdx >= 0 && monthIdx <= 11
                  ? `${day} ${MONTHS_RU[monthIdx]} ${year}`
                  : raw;
            } else {
              validUntilLine = raw;
            }
          }
          if (specialty && validUntilLine) {
            return `${specialty}, до ${validUntilLine}`;
          }
          if (specialty) return specialty;
          if (validUntilLine) return `до ${validUntilLine}`;
          return "";
        })
        .filter(Boolean);
    },
    mainDepartment() {
      return this.normalizeDeptLabel(this.person.mainDepartment);
    },
    subDepartment() {
      const sub = this.normalizeDeptLabel(this.person.subDepartment);
      const dept = this.normalizeDeptLabel(this.person.department);
      if (sub && sub !== this.mainDepartment) {
        return sub;
      }
      if (dept && dept !== this.mainDepartment) {
        return dept;
      }
      return "";
    },
    openingHours() {
      return String(this.person.openingHours || this.person.opening || "").trim();
    },
  },
  watch: {
    "person.id": {
      immediate: true,
      handler() {
        this.loadReviews();
      },
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    formatIsoDateRu(raw) {
      const str = String(raw || "").trim();
      if (!str) return "";
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return str;
      const day = Number(m[3]);
      const monthIdx = Number(m[2]) - 1;
      const year = m[1];
      if (monthIdx < 0 || monthIdx > 11) return str;
      return `${day} ${MONTHS_RU[monthIdx]} ${year}`;
    },
    normalizeDeptLabel(value) {
      if (value == null || value === "") {
        return "";
      }
      if (typeof value === "object") {
        return String(value.title || value.name || "").trim();
      }
      return String(value).trim();
    },
    close() {
      this.$emit("close");
    },
    onKeydown(e) {
      if (e.key === "Escape") {
        this.close();
      }
    },
    unwrapList(response) {
      const data = response && response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray(data.result)) return data.result;
      return [];
    },
    async loadReviews() {
      const id = this.person && this.person.id;
      if (!id) {
        this.personReviews = [];
        return;
      }
      this.reviewsLoading = true;
      try {
        const response = await reviewsApi.getReviews({ employeeId: id });
        this.personReviews = this.unwrapList(response).slice(0, 3);
      } catch (e) {
        console.warn(e);
        this.personReviews = [];
      } finally {
        this.reviewsLoading = false;
      }
    },
    truncateReview(text) {
      const t = String(text || "").trim();
      if (t.length <= 160) return t;
      return t.slice(0, 160).trim() + "…";
    },
    formatReviewDate(raw) {
      if (!raw) return "";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return String(raw);
      return `${d.getDate()} ${MONTHS_RU[d.getMonth()]} ${d.getFullYear()}`;
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;
$text-dark: #3d4654;
$text-muted: #6b7280;

.staff-person-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.72);
}

.staff-person-modal__card {
  position: relative;
  width: min(860px, 100%);
  max-height: 90vh;
  overflow: auto;
  padding: 28px 28px 24px;
  background: #fff;
  border-radius: 16px;
  box-sizing: border-box;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
}

.staff-person-modal__close {
  position: absolute;
  top: 10px;
  right: 14px;
  border: none;
  background: transparent;
  font-size: 28px;
  line-height: 1;
  color: $text-muted;
  cursor: pointer;

  &:hover {
    color: $text-dark;
  }
}

.staff-person-modal__layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
  }
}

.staff-person-modal__photo {
  flex: 0 0 320px;
  width: 320px;
  max-height: 70vh;
  height: auto;
  border-radius: 12px;
  overflow: hidden;
  background: #f4f4f4;

  img {
    width: 100%;
    height: auto;
    max-height: 70vh;
    object-fit: contain;
    object-position: top center;
    display: block;
  }

  @media (max-width: 640px) {
    flex: 0 1 auto;
    width: min(320px, 100%);
  }
}

.staff-person-modal__info {
  flex: 1;
  min-width: 0;
  padding-right: 20px;

  @media (max-width: 640px) {
    width: 100%;
    padding-right: 0;
  }
}

.staff-person-modal__name {
  display: block;
  margin: 0 0 16px;
  font-family: "TTDrugs", sans-serif;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.25;
  color: $green-text;
}

.staff-person-modal__fields {
  margin: 0;
}

.staff-person-modal__row {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0 0 10px;
  font-family: "Circe", sans-serif;
  font-size: 16px;
  line-height: 1.4;
  color: $text-dark;

  dt {
    margin: 0;
    font-weight: 700;
    color: $text-dark;
  }

  dd {
    margin: 0;
    font-weight: 400;
    color: $text-dark;
  }
}

.staff-person-modal__row--section {
  margin-top: 14px;
  margin-bottom: 6px;

  dt {
    width: 100%;
    font-weight: 700;
  }

  dd {
    display: none;
  }
}

.staff-person-modal__accreditation-item {
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(61, 70, 84, 0.12);

  &:last-child {
    border-bottom: none;
  }
}

.staff-person-modal__row--accreditation {
  align-items: flex-start;

  dd {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}

.staff-person-modal__row--diploma {
  align-items: flex-start;

  dd {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
}

.staff-person-modal__accreditation-line,
.staff-person-modal__diploma-line {
  display: block;
}

.staff-person-modal__reviews {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #e8ece8;
}

.staff-person-modal__reviews-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.staff-person-modal__reviews-title {
  margin: 0;
  font-family: "TTDrugs", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: $green-text;
}

.staff-person-modal__reviews-link,
.staff-person-modal__reviews-cta {
  font-family: "Circe", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: $green-text;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.staff-person-modal__reviews-cta {
  display: inline-block;
  margin-top: 12px;
}

.staff-person-modal__reviews-empty {
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 14px;
  color: $text-muted;
}

.staff-person-modal__review {
  margin-bottom: 12px;
}

.staff-person-modal__review-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 4px;
  font-family: "Circe", sans-serif;
  font-size: 13px;
  color: $text-muted;
}

.staff-person-modal__review-stars span {
  color: #d9d9d9;
  &.is-active {
    color: $green;
  }
}

.staff-person-modal__review-text {
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: $text-dark;
}
</style>
